/**
 * RunWorkoutPickerModal — coach picks which workout to launch on the tablet
 * for a given athlete. Fetches /api/coach/athletes/{aid}/runnable-workouts,
 * filters by query string, opens /coach/athletes/{aid}/run/{wid} on click.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Search, Dumbbell, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABEL = {
    strength: "Strength",
    power: "Power",
    speed: "Speed",
    agility: "Agility",
    conditioning: "Conditioning",
    mobility: "Mobility",
    plyometric: "Plyometric",
};

export default function RunWorkoutPickerModal({ athleteId, athleteName, open, onClose }) {
    const navigate = useNavigate();
    const [workouts, setWorkouts] = useState([]);
    const [busy, setBusy] = useState(true);
    const [q, setQ] = useState("");

    useEffect(() => {
        if (!open) return;
        setBusy(true);
        (async () => {
            try {
                const r = await axios.get(
                    `${API}/coach/athletes/${athleteId}/runnable-workouts`,
                    { withCredentials: true },
                );
                setWorkouts(r.data?.workouts || []);
            } catch (e) {
                toast.error("Couldn't load workouts", { description: e?.response?.data?.detail });
            } finally {
                setBusy(false);
            }
        })();
    }, [athleteId, open]);

    if (!open) return null;
    const filtered = workouts.filter((w) =>
        !q || w.title.toLowerCase().includes(q.toLowerCase()) ||
        (w.category || "").toLowerCase().includes(q.toLowerCase())
    );

    return (
        <div
            data-testid="run-workout-picker"
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
            >
                <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Tablet mode</div>
                        <h4 className="font-display text-xl tracking-wider mt-0.5">
                            Run workout for {athleteName}
                        </h4>
                    </div>
                    <button type="button" data-testid="picker-close" onClick={onClose} className="text-zinc-400 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                    <Search className="w-4 h-4 text-zinc-400" />
                    <input
                        data-testid="picker-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Filter by name or category…"
                        className="flex-1 bg-transparent outline-none text-sm text-white placeholder-zinc-500"
                    />
                </div>

                <div className="overflow-y-auto flex-1">
                    {busy ? (
                        <div className="p-10 flex items-center justify-center text-zinc-300">
                            <Loader2 className="w-5 h-5 animate-spin" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-10 text-center text-sm text-zinc-300">
                            <Dumbbell className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                            {workouts.length === 0 ? "No workouts in the library yet." : "No matches."}
                        </div>
                    ) : (
                        <ul className="divide-y divide-zinc-900">
                            {filtered.map((w) => (
                                <li key={w.workout_id}>
                                    <button
                                        type="button"
                                        data-testid={`picker-workout-${w.workout_id}`}
                                        onClick={() => {
                                            onClose?.();
                                            navigate(`/coach/athletes/${athleteId}/run/${w.workout_id}`);
                                        }}
                                        className="w-full text-left px-5 py-4 hover:bg-zinc-900 flex items-center gap-4 transition-colors"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display text-lg tracking-wider text-white truncate">
                                                {w.title}
                                            </div>
                                            <div className="text-[11px] tracking-wider text-zinc-400 mt-0.5">
                                                {CATEGORY_LABEL[w.category] || w.category || "Workout"}
                                                {" · "}{w.block_count} block{w.block_count === 1 ? "" : "s"}
                                                {" · "}{w.exercise_count} exercise{w.exercise_count === 1 ? "" : "s"}
                                            </div>
                                        </div>
                                        <Play className="w-4 h-4 text-brand-red flex-shrink-0" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
